import { useMemo, useState } from 'react';
import {
  formatMoney,
  occurrenceState,
  occurrences,
  toWireDate,
  type Contract,
  type ContractWorklogDay,
  type LineItem,
  type Occurrence,
  type OccurrenceEconomics,
  type OccurrenceState,
} from '@forecastify/shared';
import { useDuration } from '../../duration';
import {
  DataList,
  Icon,
  type DataListColumn,
  type DataListRow,
  type MenuItem,
  type PagerState,
  type SortState,
} from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';
import { listLabels } from '../delivery/clients-screen';
import { sortInMemory } from '../sort-in-memory';
import { SectionHeader } from './detail-chrome';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Időszakok — the periods a recurring contract derives, and their hours.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `2fazis.ContractDetail.html` 161–265. **The largest thing the contract detail was missing**, and
 * the reason a recurring contract still looked nothing like the mockup: the recurrence card said
 * *Havonta · until 2026-12-31* and then nothing showed what those twelve months actually were.
 *
 * Four ideas, and they are worth stating together because each is easy to get wrong on its own:
 *
 *  1. **the periods are DERIVED** — `occurrences()` in `shared`, from the recurrence definition and
 *     the contract's window. Nothing is stored (P2-3), so editing the recurrence changes the table
 *     rather than leaving rows that disagree with their own definition;
 *  2. **their state is the CALENDAR's** — planned before the first day, active during, closed after.
 *     No column, nobody clicks anything;
 *  3. **hours reach a period through their DATE, not through a task** — every worklog under the
 *     contract is filed into the period its `workedOn` falls in. A task belongs to no period;
 *  4. **the plan per period is the line items' estimate**, which on a recurring contract is stated
 *     per period — so the bar compares like with like.
 *
 * ⚠️⚠️ **BÜDZSÉ AND BEVÉTEL ARE HERE NOW — ux-pass-2, 14 Oct.** The mockup drew them and P2-1
 * deferred them; C55 then priced every period (`GET /contracts/:id/economics`) for the period page,
 * and this table is the other place the figures were asked for. They come in as a prop and are
 * absent rather than zero when the read did not answer — a refusal hides the two columns, because a
 * `0 Ft` budget is a claim and *not allowed to see it* is not one. *Elsz. óra* and *Számlázva* stay
 * on the period's own page, where the billing lives.
 *
 * ⚠️⚠️ **PAGED, NOT WINDOWED — ux-pass-2, 14 Oct.** This was the mockup's `occWindow`: six rows
 * around the active period and a *show all* toggle. The product's list pages instead, with the
 * settled pager, and the page it OPENS on is the one holding the active period — so the useful rows
 * are still the visible ones, without a second mode to explain.
 */

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · A period row's menu — the phase 2 mockup's `occMenu`.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Open, open full, export its hours (CSV or XLSX), open them in *Időlogok*, copy a one-line summary,
 * and reopen a closed period for late entry — or close it again. Every act is the route's; this
 * only says which period. ⚠️ Reopening is offered exactly when the period page offers it: closed,
 * and short of its plan.
 */
export interface PeriodMenuHandlers {
  readonly onOpenFull: (period: Occurrence) => void;
  readonly onExport: (period: Occurrence, format: 'csv' | 'xlsx') => void;
  readonly onOpenLogs: (period: Occurrence) => void;
  readonly onCopy: (text: string) => void;
  /** Absent for a reader who may not manage the contract. */
  readonly onSetOpen?: (period: Occurrence, open: boolean) => void;
}

/** A year of monthly periods per page — the window was six, and a page should hold more than that. */
const PAGE_SIZE = 12;

const STATE_KEY: Readonly<Record<OccurrenceState, MessageKey>> = {
  planned: 'occurrence.state.planned',
  active: 'occurrence.state.active',
  closed: 'occurrence.state.closed',
};

const STATE_COLOR: Readonly<Record<OccurrenceState, string>> = {
  planned: 'var(--muted-foreground)',
  active: 'var(--primary)',
  closed: 'var(--muted-foreground)',
};

/** ⚠️ The rank a state sorts by — the calendar's order, not the translated label's first letter. */
const STATE_RANK: Readonly<Record<OccurrenceState, number>> = {
  closed: 0,
  active: 1,
  planned: 2,
};

export function OccurrencesCard({
  contract,
  lineItems,
  worklogs,
  collector,
  economics,
  currencies,
  onOpenTask,
  onOpenPeriod,
  menu,
}: {
  readonly contract: Contract;
  readonly lineItems: readonly LineItem[];
  readonly worklogs: readonly ContractWorklogDay[];
  /** The contract's collector task, when it has one — the key, so the prompt can name it. */
  readonly collector: { readonly id: string; readonly key: string; readonly title: string } | null;
  /**
   * Every period, priced — `GET /contracts/:id/economics`. ⚠️ **Empty is *not loaded or not
   * allowed*, and hides the money columns**; it is never read as zero.
   */
  readonly economics: readonly OccurrenceEconomics[];
  /** The workspace's currencies, for the symbol beside each figure. */
  readonly currencies: readonly { readonly code: string; readonly symbol: string }[];
  readonly onOpenTask: (taskId: string) => void;
  /** C29 · a row opens its period. */
  readonly onOpenPeriod: (period: Occurrence) => void;
  readonly menu?: PeriodMenuHandlers;
}) {
  const { fmt } = useDuration();
  const t = useT();
  const [sort, setSort] = useState<readonly SortState[]>([]);

  const today = useMemo(() => toWireDate(new Date()), []);
  /* ⚠️ P2-33: an open-ended series runs a year past today rather than 24 from its start. */
  const periods = useMemo(
    () => occurrences(contract.recurrence, contract.endsOn, today),
    [contract.recurrence, contract.endsOn, today],
  );

  /**
   * ⚠️⚠️ **BUCKETED IN ONE PASS, NOT ONE FILTER PER ROW.** A year of periods against a busy
   * contract's worklogs is 12 × n comparisons per render otherwise, and this component re-renders
   * on every live invalidation the contract subscribes to.
   */
  const hoursByIndex = useMemo(() => {
    const totals = new Map<number, number>();
    for (const log of worklogs) {
      const period = periods.find((one) => log.workedOn >= one.from && log.workedOn <= one.to);
      /* ⚠️ Hours outside every period are DROPPED rather than filed into the nearest — see
         `occurrenceOf`. Logging before a contract started is possible, and quietly adding those to
         #1 would be wrong in the direction nobody checks. */
      if (period === undefined) continue;
      totals.set(period.index, (totals.get(period.index) ?? 0) + Number(log.hours));
    }
    return totals;
  }, [worklogs, periods]);

  /* ⚠️ Matched on the START DATE — a period has no id (P2-3), and the endpoint keys it the same way. */
  const moneyByStart = useMemo(
    () => new Map(economics.map((one) => [one.startsOn, one])),
    [economics],
  );
  const showMoney = economics.length > 0;

  /**
   * The plan for one period: the sum of the line items' estimates.
   *
   * ⚠️⚠️ **NO DIVISION BY THE PERIOD COUNT, AND THE MOCKUP DOES DIVIDE.** Its `perOcc` is
   * `total / occ.length` because it stores a line item's estimate as the whole commitment; `0050`
   * stores the PER-PERIOD figure instead, which is the same number the dialog asks for. So the sum
   * already is the plan for one period, and dividing would give a twelfth of it.
   */
  const planPerPeriod = useMemo(
    () => lineItems.reduce((sum, item) => sum + Number(item.estimateHours), 0),
    [lineItems],
  );

  const money = (value: string | undefined, code: string | null | undefined): string => {
    if (value === undefined || code === null || code === undefined) return '';
    const symbol = currencies.find((one) => one.code === code)?.symbol ?? code;
    return formatMoney(Number(value), { code, symbol });
  };

  const sorted = sortInMemory(periods, sort, {
    index: (period) => period.index,
    period: (period) => period.from,
    state: (period) => STATE_RANK[occurrenceState(period, today)],
    tracked: (period) => hoursByIndex.get(period.index) ?? 0,
    budget: (period) => {
      const figure = moneyByStart.get(period.from)?.budget;
      return figure === undefined ? null : Number(figure);
    },
    revenue: (period) => {
      const figure = moneyByStart.get(period.from)?.revenue;
      return figure === undefined ? null : Number(figure);
    },
    cost: (period) => {
      const figure = moneyByStart.get(period.from)?.cost;
      return figure === undefined ? null : Number(figure);
    },
  });

  /**
   * ⚠️⚠️ **THE PAGE IT OPENS ON HOLDS THE ACTIVE PERIOD.** A contract signed two years ago would
   * otherwise open on periods nobody has thought about since. With none active, the window's own
   * two cases: a contract entirely in the future opens at its start, one entirely in the past at
   * its end. `wanted` is null until somebody pages, and a new sort clears it again — the anchor
   * moves with the order, so the active period is still on the page the sort lands on.
   */
  const [wanted, setWanted] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState(() => JSON.stringify(sort));
  if (sortKey !== JSON.stringify(sort)) {
    setSortKey(JSON.stringify(sort));
    setWanted(null);
  }

  if (contract.recurrence === null || periods.length === 0) return null;

  const activeIndex = periods.findIndex((one) => occurrenceState(one, today) === 'active');
  const anchor =
    activeIndex >= 0
      ? periods[activeIndex]
      : (periods[0]?.from ?? '') > today
        ? periods[0]
        : periods.at(-1);
  const anchorAt = Math.max(
    0,
    sorted.findIndex((one) => one === anchor),
  );

  const count = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const index = Math.min(Math.max(0, wanted ?? Math.floor(anchorAt / PAGE_SIZE)), count - 1);
  const goTo = (to: number): void => {
    setWanted(Math.min(Math.max(0, to), count - 1));
  };
  const page: PagerState = {
    hasPrevious: index > 0,
    hasNext: index + 1 < count,
    onPrevious: () => {
      goTo(index - 1);
    },
    onNext: () => {
      goTo(index + 1);
    },
    index,
    count,
    total: sorted.length,
    onGoTo: goTo,
    pageSize: PAGE_SIZE,
  };

  const rows: DataListRow[] = sorted
    .slice(index * PAGE_SIZE, index * PAGE_SIZE + PAGE_SIZE)
    .map((period) => {
      const state = occurrenceState(period, today);
      const hours = hoursByIndex.get(period.index) ?? 0;
      const pct = planPerPeriod > 0 ? Math.min(100, Math.round((hours / planPerPeriod) * 100)) : 0;
      const over = planPerPeriod > 0 && hours > planPerPeriod;
      const figures = moneyByStart.get(period.from);
      const closed = state === 'closed';

      return {
        id: String(period.index),
        cells: {
          index: { text: `#${String(period.index)}`, mono: true, muted: true },
          period: {
            text: `${period.from} – ${period.to}`,
            mono: true,
            /* ⚠️ The row the reader is living in reads heavier, and a past one quieter — the
               shading the hand-built row carried, said in the cell since a list row takes none. */
            bold: state === 'active',
            muted: closed,
            /* ⚠️ *részleges* means the CONTRACT cut this period short, not that the month is. */
            ...(period.partial ? { sub: t('occurrence.partial') } : {}),
          },
          state: {
            dot: STATE_COLOR[state],
            text: t(STATE_KEY[state]),
            title: t(TIP_KEY[state]),
            muted: closed,
          },
          plan: {
            text: planPerPeriod > 0 ? fmt(String(planPerPeriod)) : '–',
            mono: true,
            muted: true,
            align: 'right' as const,
          },
          effort: {
            progress: {
              value: pct,
              label: t('occurrence.effort'),
              ...(over ? { color: 'var(--destructive)' } : {}),
            },
          },
          tracked: {
            text: hours > 0 ? fmt(String(hours)) : '–',
            mono: true,
            muted: closed,
            align: 'right' as const,
          },
          budget: {
            text: money(figures?.budget, figures?.currencyCode),
            mono: true,
            muted: closed,
            align: 'right' as const,
          },
          revenue: {
            text: money(figures?.revenue, figures?.currencyCode),
            mono: true,
            muted: closed,
            align: 'right' as const,
          },
          /* 17 Oct 2026 · *Költés* — the period's logged hours at cost. */
          cost: {
            text: money(figures?.cost, figures?.currencyCode),
            mono: true,
            muted: closed,
            align: 'right' as const,
          },
        },
      };
    });

  /** The mockup's `occMenu`, item for item. */
  const rowMenu = (id: string): MenuItem[] => {
    const period = periods.find((one) => String(one.index) === id);
    if (period === undefined || menu === undefined) return [];
    const state = occurrenceState(period, today);
    const hours = hoursByIndex.get(period.index) ?? 0;
    const missing = planPerPeriod - hours;
    const reopened = state === 'closed' && contract.reopenedPeriods.includes(period.from);
    const range = `${period.from} – ${period.to}`;
    const empty = hours <= 0.004;
    const setOpen = menu.onSetOpen;
    return [
      {
        type: 'label' as const,
        label: `#${String(period.index)} · ${range} · ${t(STATE_KEY[state])}`,
      },
      {
        label: t('period.menu.open'),
        icon: 'arrow-right',
        onSelect: () => {
          onOpenPeriod(period);
        },
      },
      {
        label: t('period.menu.openFull'),
        icon: 'maximize',
        onSelect: () => {
          menu.onOpenFull(period);
        },
      },
      { type: 'separator' as const },
      {
        label: t('period.menu.exportCsv'),
        icon: 'download',
        disabled: empty,
        onSelect: () => {
          menu.onExport(period, 'csv');
        },
      },
      {
        label: t('period.menu.exportXlsx'),
        icon: 'file-spreadsheet',
        disabled: empty,
        onSelect: () => {
          menu.onExport(period, 'xlsx');
        },
      },
      { type: 'separator' as const },
      {
        label: t('period.menu.openLogs'),
        icon: 'list-checks',
        disabled: empty,
        onSelect: () => {
          menu.onOpenLogs(period);
        },
      },
      {
        label: t('period.menu.copy'),
        icon: 'copy',
        onSelect: () => {
          menu.onCopy(
            `${contract.identifier} #${String(period.index)} · ${range} · ${fmt(String(hours))}`,
          );
        },
      },
      ...(setOpen === undefined
        ? []
        : [
            { type: 'separator' as const },
            state === 'closed' && !reopened
              ? {
                  label: t('period.reopen'),
                  icon: 'refresh-cw' as const,
                  hint:
                    missing > 0.004
                      ? t('period.menu.missing', { hours: fmt(String(missing)) })
                      : t('period.menu.noneMissing'),
                  /* 30 Oct 2026 · any ended period reopens, whatever its hours — the hint still
                     says how many are missing. `period-detail.tsx` argues it. */
                  onSelect: () => {
                    setOpen(period, true);
                  },
                }
              : reopened
                ? {
                    label: t('period.close'),
                    icon: 'circle-check' as const,
                    hint:
                      missing > 0.004
                        ? t('period.menu.stillMissing', { hours: fmt(String(missing)) })
                        : t('period.menu.planMet'),
                    onSelect: () => {
                      setOpen(period, false);
                    },
                  }
                : { label: t('period.menu.byTime'), icon: 'clock' as const, disabled: true },
          ]),
    ];
  };

  const columns: DataListColumn[] = [
    { key: 'index', label: '#', width: 56, sortKey: 'index' },
    { key: 'period', label: t('contract.occurrence'), sortKey: 'period' },
    { key: 'state', label: t('contract.column.status'), width: 110, sortKey: 'state' },
    { key: 'plan', label: t('occurrence.plan'), width: 72, align: 'right' },
    { key: 'effort', label: t('occurrence.effort'), width: 110 },
    {
      key: 'tracked',
      label: t('contract.column.tracked'),
      width: 84,
      align: 'right',
      sortKey: 'tracked',
    },
    ...(showMoney
      ? [
          {
            key: 'budget',
            label: t('lineItem.money.budget'),
            width: 110,
            align: 'right' as const,
            sortKey: 'budget',
          },
          {
            key: 'revenue',
            label: t('lineItem.money.revenue'),
            width: 110,
            align: 'right' as const,
            sortKey: 'revenue',
          },
          {
            key: 'cost',
            label: t('money.spend'),
            width: 110,
            align: 'right' as const,
            sortKey: 'cost',
          },
        ]
      : []),
  ];

  return (
    <section>
      <SectionHeader
        title={t('contract.occurrences')}
        note={t('occurrence.count', { count: periods.length })}
      />

      <div className="mt-1.5 overflow-hidden rounded-lg border">
        {/* ⚠️ **No toolbar**: two dozen derived rows need no search, and a column chooser over a
            table whose columns are the period's whole meaning offers to take that meaning away. */}
        <DataList
          columnsKey="contract-periods"
          card={{
            title: 'period',
            context: 'index',
            defaults: { badge: 'state', details: ['plan', 'tracked', 'budget', 'revenue'] },
          }}
          toolbar={false}
          selectable={false}
          columns={columns}
          rows={rows}
          page={page}
          sort={sort}
          onSortChange={setSort}
          onRowClick={(id) => {
            const period = periods.find((one) => String(one.index) === id);
            if (period !== undefined) onOpenPeriod(period);
          }}
          {...(menu === undefined ? {} : { rowMenu })}
          empty={{ icon: 'calendar', title: t('contract.occurrences'), body: '' }}
          labels={listLabels(t)}
        />
      </div>

      {/* ⚠️ **Creating the collector task is withdrawn from the screen for now** (14 Oct 2026,
          the product owner: "we'll add it back later"). An existing one still shows here; the
          endpoint and P2-10 stand, and the prompt that offered one is in git history. */}
      {collector === null ? null : (
        <button
          type="button"
          className="hover:bg-accent/40 mt-2.5 flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors"
          onClick={() => {
            onOpenTask(collector.id);
          }}
        >
          <Icon name="repeat" size={13} className="text-muted-foreground shrink-0" />
          <span className="text-muted-foreground shrink-0 text-[11.5px]">{collector.key}</span>
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium">
            {collector.title}
          </span>
          <span className="text-muted-foreground shrink-0 text-[11px]">
            {t('occurrence.collectorNote')}
          </span>
        </button>
      )}
    </section>
  );
}

const TIP_KEY: Readonly<Record<OccurrenceState, MessageKey>> = {
  planned: 'occurrence.tip.planned',
  active: 'occurrence.tip.active',
  closed: 'occurrence.tip.closed',
};
